import React, { useEffect, useState } from 'react';
import { CALL_OUTCOMES, isoInDays, outcomeByKey } from '../utils/callList';
import { logDealCall } from '../services/AcquisitionService';
import { logLeadCall } from '../services/LeadService';
import { nextStage } from '../utils/salesPipeline';
import { useToast } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import '../styles/CallList.css';

/** Log a call and decide the next step in the same action, so nothing can be logged as "called"
 *  while staying overdue. "Kein Interesse" is the only outcome that removes the date. */
export default function CallOutcomeModal({ item, onClose, onSaved }) {
    const toast = useToast();
    const [outcome, setOutcome] = useState('not_reached');
    const [note, setNote] = useState('');
    const [followUp, setFollowUp] = useState(isoInDays(1));
    const [lostReason, setLostReason] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const chosen = outcomeByKey(outcome);
        setFollowUp(chosen?.days === null ? '' : isoInDays(chosen?.days ?? 1));
    }, [outcome]);

    if (!item) return null;

    const chosen = outcomeByKey(outcome);
    const isDeal = item.kind === 'deal';
    const advance = chosen?.advancesStage && isDeal ? nextStage(item.stage) : null;

    const handleSubmit = async () => {
        const label = chosen?.label ?? 'Anruf';
        const content = note.trim() ? `${label} — ${note.trim()}` : label;
        setSaving(true);
        try {
            if (isDeal) {
                await logDealCall(item.id, {
                    type: 'call',
                    content,
                    nextFollowUpDate: followUp || null,
                    clearFollowUp: !followUp,
                    status: chosen?.closes ? 'Lost' : advance?.key,
                    lostReason: chosen?.closes ? (lostReason.trim() || 'Kein Interesse') : null,
                });
            } else {
                await logLeadCall(item.id, {
                    note: content,
                    nextFollowUpDate: followUp || null,
                    clearFollowUp: !followUp,
                    status: chosen?.closes ? 'no_interest' : undefined,
                });
            }
            onSaved?.();
        } catch (err) {
            toast(extractApiError(err, 'Anruf konnte nicht gespeichert werden.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal call-modal">
                <div className="modal-header">
                    <div>
                        <h3>Anruf protokollieren</h3>
                        <p className="call-modal-sub">
                            {item.company}{item.contact ? ` · ${item.contact}` : ''}
                            {item.phone ? ` · ${item.phone}` : ''}
                        </p>
                    </div>
                    <button className="modal-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    <div className="form-section-label">Ergebnis</div>
                    <div className="call-outcomes">
                        {CALL_OUTCOMES.map(option => (
                            <button
                                key={option.key}
                                type="button"
                                className={`call-outcome${outcome === option.key ? ' is-selected' : ''}${option.closes ? ' is-negative' : ''}`}
                                onClick={() => setOutcome(option.key)}
                            >
                                <span className="call-outcome-label">{option.label}</span>
                                <span className="call-outcome-hint">{option.hint}</span>
                            </button>
                        ))}
                    </div>

                    {advance && (
                        <p className="acq-hint">
                            Phase rückt auf <strong>„{advance.label}“</strong> vor.
                        </p>
                    )}

                    <div className="form-field">
                        <label className="form-label">Notiz</label>
                        <textarea
                            className="input input-textarea"
                            rows={3}
                            placeholder="Was wurde besprochen? Was ist der nächste Schritt?"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>

                    {chosen?.closes ? (
                        <div className="form-field">
                            <label className="form-label">Grund</label>
                            <input
                                type="text"
                                className="input"
                                placeholder="z.B. kein Bedarf, Budget gestrichen, Wettbewerber"
                                value={lostReason}
                                onChange={(e) => setLostReason(e.target.value)}
                            />
                            <span className="acq-hint">
                                {isDeal
                                    ? 'Deal wird auf „Verloren“ gesetzt und verlässt die Anrufliste.'
                                    : 'Lead wird auf „Kein Interesse“ gesetzt und verlässt die Anrufliste.'}
                            </span>
                        </div>
                    ) : (
                        <div className="form-field">
                            <label className="form-label">Nächste Wiedervorlage</label>
                            <input
                                type="date"
                                className="input"
                                value={followUp}
                                onChange={(e) => setFollowUp(e.target.value)}
                            />
                            <span className="acq-hint">
                                {followUp
                                    ? 'Danach verschwindet der Eintrag aus der heutigen Liste.'
                                    : 'Ohne Datum bleibt der Eintrag als „ohne Termin“ offen.'}
                            </span>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="button" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Speichern…' : 'Anruf speichern'}
                    </button>
                    <button className="button button-secondary" onClick={onClose}>Abbrechen</button>
                </div>
            </div>
        </div>
    );
}
